import { Dispatch } from 'redux'
import config from 'app/config/config'
import { client } from 'app/shared/api/client'
import { loadCurrent } from 'app/state/utils/generation'
import { ErrorNotification } from 'app/views/components/notifications/notification'
import {
  entityScreenLoadFailed,
  getEntityScreen,
  startLoadingEntityScreen,
  stopLoadingEntityScreen,
} from './actions'
import { EntityScreenState } from './reducer'

type GetState = () => { entityScreen: EntityScreenState }

/** One request at a time: a strict-mode double mount, or a page effect firing during a load, joins it. */
let inFlight: Promise<unknown> | null = null

/**
 * The whole Entity Management grid in one request: every entity in both its
 * states, with the entity type and profile names already on the row. The
 * backend reads the two entity templates in one PQS statement plus one small
 * name lookup, so the screen no longer loads both entity lists as whole
 * records, plus the type and profile lists, just to draw a row.
 *
 * The response carries the store's generation at the time of the request; if
 * a write bumped it meanwhile the reducer drops the rows and this reads once
 * more (utils/generation.ts). On failure the store keeps what it had, the user
 * is told, and the loader still stops.
 */
export const loadEntityScreen = () => (dispatch: Dispatch, getState?: GetState) => {
  if (inFlight) return inFlight
  const generation = () => getState?.().entityScreen.generation ?? 0
  inFlight = loadCurrent(generation, g => load(g, dispatch)).finally(() => {
    // in flight is cleared BEFORE the loader stops: the grid's "reload once
    // while stale" effect fires on the loader stopping, and must start a new
    // read rather than join this finished one
    inFlight = null
    dispatch(stopLoadingEntityScreen())
  })
  return inFlight
}

const load = async (generation: number, dispatch: Dispatch) => {
  const apiServerUrl = config.backendUrl
  try {
    dispatch(startLoadingEntityScreen())
    const response = await client.get(`${apiServerUrl}/api/entity-management/screen`)
    return dispatch(getEntityScreen(response.data, generation))
  } catch (e: any) {
    dispatch(entityScreenLoadFailed())
    ErrorNotification({
      title: 'Could not load entities',
      message:
        e?.response?.data?.error ??
        'The Entities grid could not be loaded. Reload the page to try again.',
    })
  }
}
